import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { getSocket } from "../../lib/socket";
import { timeAgo } from "../../lib/format";
import { useAuth } from "../../stores/auth";
import { ProductImage } from "../ProductCard";
import { TierBadge } from "../storefront/TierBadge";
import { Marquee } from "../fx/Marquee";
import type { FeedPull } from "../storefront/LivePullTicker";

/**
 * Always-moving band of the most recent pack pulls across the whole store.
 * New pulls arrive over the socket and slot straight into the loop.
 */
export function LivePullMarquee({ className = "" }: { className?: string }) {
  const { moduleEnabled } = useAuth();
  const [pulls, setPulls] = useState<FeedPull[]>([]);

  const { data } = useQuery({
    queryKey: ["pack-feed"],
    queryFn: () => api<{ items: FeedPull[] }>("/packs/feed"),
    enabled: moduleEnabled("packs"),
  });

  useEffect(() => {
    if (data) setPulls(data.items.slice(0, 14));
  }, [data]);

  useEffect(() => {
    const socket = getSocket();
    const onPull = (pull: FeedPull) => setPulls((prev) => [pull, ...prev].slice(0, 14));
    socket.on("feed:pack-pull", onPull);
    return () => {
      socket.off("feed:pack-pull", onPull);
    };
  }, []);

  if (!moduleEnabled("packs") || pulls.length < 3) return null;

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-white/5 bg-surface/50 py-2.5 ${className}`}>
      <div className="mb-2 flex items-center gap-1.5 px-3">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-ring" />
        <span className="font-display text-[9px] font-bold uppercase tracking-[0.28em] text-white/45">
          Live pulls
        </span>
      </div>
      <Marquee durationSec={44}>
        {pulls.map((pull, i) => (
          <div
            key={pull.id ?? `${pull.at}-${i}`}
            className="flex w-60 items-center gap-2.5 rounded-xl bg-white/[0.04] p-2"
          >
            <ProductImage src={pull.productImage} alt="" className="h-9 w-9 shrink-0 rounded-lg" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] font-semibold text-white/90">{pull.productName}</p>
              <div className="mt-0.5 flex items-center gap-1">
                <TierBadge name={pull.tierName} color={pull.tierColor} size="sm" />
                <span className="truncate text-[9px] text-white/40">
                  {pull.handle} · {timeAgo(pull.at)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </Marquee>
    </div>
  );
}
